import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Formik, Form, ErrorMessage, Field } from "formik";
import * as Yup from "yup";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";
import {
  get_interpreter_by_id,
  get_interpreter_tiers,
  update_interpreter,
  disable_tier4_interpreter,
  get_interpreter_queues,
} from "../../api/InterpreterApi";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  Check,
  ChevronsUpDown,
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Undo,
  Redo,
} from "lucide-react";
import { cn } from "../../lib/utils";
import countries from "../../assets/countries.json";
import { useAuth } from "../../context/AuthContext";
const extensions = [StarterKit];
import * as Sentry from "@sentry/react";
const { logger } = Sentry;
const TiptapMenuBar = ({ editor }) => {
  if (!editor) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2 p-2 border-b border-gray-200 bg-gray-50 rounded-t-md">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={editor.isActive("bold") ? "bg-gray-200" : ""}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={editor.isActive("italic") ? "bg-gray-200" : ""}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={editor.isActive("bulletList") ? "bg-gray-200" : ""}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={editor.isActive("orderedList") ? "bg-gray-200" : ""}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={
          editor.isActive("heading", { level: 1 }) ? "bg-gray-200" : ""
        }
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        <Heading1 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={
          editor.isActive("heading", { level: 2 }) ? "bg-gray-200" : ""
        }
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={
          editor.isActive("heading", { level: 3 }) ? "bg-gray-200" : ""
        }
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
      >
        <Undo className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
      >
        <Redo className="h-4 w-4" />
      </Button>
    </div>
  );
};

const Tiptap = ({ value, onChange }) => {
  const editor = useEditor({
    extensions,
    content: value || "<p></p>",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  return (
    <div className="border rounded-md">
      <TiptapMenuBar editor={editor} />
      <EditorContent
        editor={editor}
        className="prose max-w-none p-4 min-h-[200px] outline-none w-full"
      />
    </div>
  );
};
import { Loader2, AlertTriangle, UserMinus } from "lucide-react";
import { GlobalContext } from "../../App";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const validationSchema = Yup.object().shape({
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required"),
  phone_number: Yup.string()
    .matches(
      /^\+[1-9]\d{1,14}$/,
      "Phone number must be in valid E.164 format starting with '+'"
    )
    .nullable(),
  interpreter_home_number: Yup.string(),
  interpreter_special_id: Yup.string().required("Interpreter ID is required"),
  // languages: Yup.array().min(1, "At least one language is required"),
  country: Yup.string(),
  notes: Yup.string(),
  video_rate: Yup.number()
    .typeError("Video rate must be a number")
    .min(0, "Video rate cannot be negative"),
  tierRates: Yup.object().shape({
    1: Yup.object().shape({
      voice_rate: Yup.number()
        .typeError("Tier 1 rate must be a number")
        .min(0, "Tier 1 rate cannot be negative")
        .required("Tier 1 rate is required"),
    }),
    2: Yup.object().shape({
      voice_rate: Yup.number()
        .typeError("Tier 2 rate must be a number")
        .min(0, "Tier 2 rate cannot be negative")
        .required("Tier 2 rate is required"),
    }),
    3: Yup.object().shape({
      voice_rate: Yup.number()
        .typeError("Tier 3 rate must be a number")
        .min(0, "Tier 3 rate cannot be negative")
        .required("Tier 3 rate is required"),
    }),
    4: Yup.object().shape({
      voice_rate: Yup.number()
        .typeError("Tier 4 rate must be a number")
        .min(0, "Tier 4 rate cannot be negative")
        .required("Tier 4 rate is required"),
    }),
  }),
});

const EditInterpreterForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [interpreter, setInterpreter] = useState(null);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [openLanguageSelector, setOpenLanguageSelector] = useState(false);
  const [openCountrySelector, setOpenCountrySelector] = useState(false);

  const [tiers, setTiers] = useState([]);
  const [isOnlyTier4, setIsOnlyTier4] = useState(false);
  const [error, setError] = useState(null);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [disableLoading, setDisableLoading] = useState(false);
  const [queues, setQueues] = useState([]);
  const { setToastOpen, setToastMessage, setToastSeverity, languages } =
    useContext(GlobalContext);
  const { user } = useAuth();
  useEffect(() => {
    const fetchInterpreter = async () => {
      try {
        setLoading(true);
        const response = await get_interpreter_by_id(id);
        const tiersResponse = await get_interpreter_tiers(id);
        const queuesResponse = await get_interpreter_queues(
          response.data.z_user_id
        );
        // logger.info("tiersResponse", tiersResponse);
        setTiers(tiersResponse.data);
        setQueues(queuesResponse.data);
        if (response && response.data) {
          setIsOnlyTier4(response.is_only_tier_4);
          // logger.info("is_only_tier_4", response.is_only_tier_4);
          const interpreterData = response.data;
          console.log(interpreterData);
          // Set the languages from the response
          if (response.languages) {
            setSelectedLanguages(response.languages);
            // Update the interpreter object with languages from response
            interpreterData.languages = response.languages;
          }
          // Set interpreter_home_number if it exists in the response
          if (response.interpreter_home_number) {
            interpreterData.interpreter_home_number =
              response.interpreter_home_number;
          }
          setInterpreter(interpreterData);
        } else {
          setError("Failed to load interpreter data");
        }
      } catch (err) {
        console.error("Error fetching interpreter:", err);
        setError("An error occurred while fetching interpreter data");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchInterpreter();
    }
  }, [id]);

  const updateInterpreter = async (interpreterData) => {
    try {
      console.log("Calling update_interpreter API with data:", interpreterData);
      const response = await update_interpreter(interpreterData);
      console.log("update_interpreter API response:", response);
      return response;
    } catch (error) {
      console.error("Error updating interpreter:", error);
      throw error;
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      console.log("Form submission started", values);

      // Format the data for API submission
      const interpreterData = {
        interpreter_id: id,
        interpreter_first_name: values.first_name,
        interpreter_last_name: values.last_name,
        interpreter_phone_number: values.phone_number,
        interpreter_home_number: values.interpreter_home_number || null,
        interpreter_special_id: values.interpreter_special_id,
        interpreter_address: values.interpreter_address,
        interpreter_city: values.interpreter_city,
        interpreter_state: values.interpreter_state,
        interpreter_zip: values.interpreter_zip,
        interpreter_email: values.interpreter_email,
        languages: JSON.stringify(values.languages),
        country: values.country || null,
        notes: values.notes || null,
        video_rate: values.video_rate,
        z_user_id: interpreter?.z_user_id || null,
        tier_rates: [
          {
            tier: 1,
            voice_rate: Number(values.tierRates[1].voice_rate),
            video_rate: Number(values.tierRates[1].video_rate) || 0,
          },
          {
            tier: 2,
            voice_rate: Number(values.tierRates[2].voice_rate),
            video_rate: Number(values.tierRates[2].video_rate) || 0,
          },
          {
            tier: 3,
            voice_rate: Number(values.tierRates[3].voice_rate),
            video_rate: Number(values.tierRates[3].video_rate) || 0,
          },
          {
            tier: 4,
            voice_rate: Number(values.tierRates[4].voice_rate),
            video_rate: Number(values.tierRates[4].video_rate) || 0,
          },
        ],
      };

      // Call API to update interpreter
      const response = await updateInterpreter(interpreterData);
      console.log("API response:", response);

      setToastOpen(true);
      setToastMessage("Interpreter updated successfully");
      setToastSeverity("success");
    } catch (error) {
      console.error("Error submitting form:", error);
      setToastOpen(true);
      setToastMessage(
        "Error submitting form: " + (error.message || "Unknown error")
      );
      setToastSeverity("error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading interpreter data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div
          className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative"
          role="alert"
        >
          <strong className="font-bold">Error:</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
        <div className="mt-4">
          <Button onClick={() => navigate("/admin/interpreters")}>
            Back to Interpreters List
          </Button>
        </div>
      </div>
    );
  }

  // Convert the array of tier rates to the object format needed for the form
  const mapTiersToFormFormat = () => {
    const tierRatesObj = {
      1: { voice_rate: "", video_rate: "" },
      2: { voice_rate: "", video_rate: "" },
      3: { voice_rate: "", video_rate: "" },
      4: { voice_rate: "", video_rate: "" },
    };
    // If tiers array exists, map each tier to the corresponding object property
    if (tiers && tiers.length > 0) {
      tiers.forEach((tier) => {
        if (tier.interpreter_tier >= 1 && tier.interpreter_tier <= 4) {
          tierRatesObj[tier.interpreter_tier] = {
            voice_rate: tier.voice_rate ?? "",
            video_rate: tier.video_rate ?? "",
          };
        }
      });
    }
    return tierRatesObj;
  };

  const initialValues = {
    first_name: interpreter?.interpreter_first_name || "",
    last_name: interpreter?.interpreter_last_name || "",
    phone_number: interpreter?.interpreter_phone_number || "",
    interpreter_home_number: interpreter?.interpreter_home_number || "",
    interpreter_special_id: interpreter?.interpreter_special_id || "",
    interpreter_email: interpreter?.email || "",
    interpreter_address: interpreter?.interpreter_address || "",
    interpreter_city: interpreter?.interpreter_city || "",
    interpreter_state: interpreter?.interpreter_state || "",
    interpreter_zip: interpreter?.interpreter_zip || "",
    languages: selectedLanguages || interpreter?.interpreter_languages || [],
    country: interpreter?.interpreter_country || "",
    notes: interpreter?.interpreter_notes || "",
    video_rate: interpreter?.video_rate || "",
    tierRates: mapTiersToFormFormat(),
  };

  const handleDisableTier4 = async () => {
    try {
      setDisableLoading(true);
      await disable_tier4_interpreter(id);
      setToastOpen(true);
      setToastMessage("Tier 4 status disabled successfully");
      setToastSeverity("success");
      navigate("/admin/interpreters");
    } catch (error) {
      console.error("Error disabling Tier 4 status:", error);
      setToastOpen(true);
      setToastMessage(
        "Error disabling Tier 4 status: " + (error.message || "Unknown error")
      );
      setToastSeverity("error");
    } finally {
      setDisableLoading(false);
      setDisableDialogOpen(false);
    }
  };

  return (
    <div className="mx-auto pb-10">
      <Breadcrumb className="mb-6">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink onClick={() => navigate("/admin")}>
              Admin
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink onClick={() => navigate("/admin/interpreters")}>
              Interpreters
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Edit Interpreter</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <Card>
        <div className="flex justify-between items-center">
          <div className="flex-1">
            <CardHeader className="w-full">
              <CardTitle>Edit Interpreter</CardTitle>
              <CardDescription>
                Update interpreter information using the form below.
              </CardDescription>
            </CardHeader>
          </div>
          {interpreter?.interpreter_tier == "4" && (
            <div className="flex items-center gap-2 mr-6">
              <span className="inline-flex items-center rounded-md bg-purple-100 px-3 py-2 font-medium text-purple-700 text-base">
                {"Tier 4"}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setDisableDialogOpen(true)}
                title="Disable Tier 4"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <UserMinus className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
        <CardContent>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            validateOnBlur={true}
            validateOnChange={false}
            enableReinitialize
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              isSubmitting,
              setFieldValue,
            }) => (
              <Form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* First Name */}
                  <div className="space-y-2">
                    <label htmlFor="first_name" className="text-sm font-medium">
                      First Name
                    </label>
                    <Input
                      id="first_name"
                      name="first_name"
                      placeholder="Enter first name"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.first_name}
                      disabled={!isOnlyTier4}
                      className={
                        errors.first_name && touched.first_name
                          ? "border-red-500"
                          : ""
                      }
                    />
                    <ErrorMessage
                      name="first_name"
                      component="div"
                      className="text-xs text-red-500 font-light"
                    />
                  </div>

                  {/* Last Name */}
                  <div className="space-y-2">
                    <label htmlFor="last_name" className="text-sm font-medium">
                      Last Name
                    </label>
                    <Input
                      id="last_name"
                      name="last_name"
                      placeholder="Enter last name"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.last_name}
                      disabled={!isOnlyTier4}
                      className={
                        errors.last_name && touched.last_name
                          ? "border-red-500"
                          : ""
                      }
                    />
                    <ErrorMessage
                      name="last_name"
                      component="div"
                      className="text-xs text-red-500 font-light"
                    />
                  </div>

                  {/* Phone Number */}
                  <div className="space-y-2">
                    <label
                      htmlFor="phone_number"
                      className="text-sm font-medium"
                    >
                      Phone Number
                    </label>
                    <Input
                      id="phone_number"
                      name="phone_number"
                      placeholder="Enter phone number"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.phone_number}
                      className={
                        errors.phone_number && touched.phone_number
                          ? "border-red-500"
                          : ""
                      }
                    />
                    <ErrorMessage
                      name="phone_number"
                      component="div"
                      className="text-xs text-red-500 font-light"
                    />
                  </div>

                  {/* Home Number */}
                  <div className="space-y-2">
                    <label
                      htmlFor="interpreter_home_number"
                      className="text-sm font-medium"
                    >
                      Home Number (Optional)
                    </label>
                    <Input
                      id="interpreter_home_number"
                      name="interpreter_home_number"
                      placeholder="Enter home number"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.interpreter_home_number}
                      className={
                        errors.interpreter_home_number &&
                        touched.interpreter_home_number
                          ? "border-red-500"
                          : ""
                      }
                    />
                    <ErrorMessage
                      name="interpreter_home_number"
                      component="div"
                      className="text-xs text-red-500 font-light"
                    />
                  </div>

                  {/* Interpreter ID */}
                  <div className="space-y-2">
                    <label
                      htmlFor="interpreter_special_id"
                      className="text-sm font-medium"
                    >
                      Interpreter ID
                    </label>
                    <Input
                      id="interpreter_special_id"
                      name="interpreter_special_id"
                      placeholder="Enter interpreter ID"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.interpreter_special_id}
                      className={
                        errors.interpreter_special_id &&
                        touched.interpreter_special_id
                          ? "border-red-500"
                          : ""
                      }
                    />
                    <ErrorMessage
                      name="interpreter_special_id"
                      component="div"
                      className="text-xs text-red-500 font-light"
                    />
                  </div>

                  {/* Country */}
                  <div className="space-y-2">
                    <label htmlFor="country" className="text-sm font-medium">
                      Country
                    </label>
                    <Popover
                      open={openCountrySelector}
                      onOpenChange={setOpenCountrySelector}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={openCountrySelector}
                          className="w-full justify-between"
                        >
                          {values.country
                            ? countries.find((c) => c.cca2 === values.country)
                              ? countries.find((c) => c.cca2 === values.country)
                                  .name +
                                " " +
                                countries.find((c) => c.cca2 === values.country)
                                  .flag
                              : typeof values.country === "object"
                              ? values.country.name + " " + values.country.flag
                              : values.country
                            : "Select country..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0">
                        <Command>
                          <CommandInput placeholder="Search countries..." />
                          <CommandEmpty>No country found.</CommandEmpty>
                          <CommandList className="max-h-60 overflow-auto">
                            <CommandGroup>
                              {countries.map((country) => (
                                <CommandItem
                                  key={country.cca2}
                                  value={country}
                                  onSelect={() => {
                                    setFieldValue("country", country.cca2);
                                    setOpenCountrySelector(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      values.country === country.cca2
                                        ? "opacity-100"
                                        : "opacity-0"
                                    )}
                                  />
                                  {country.name} {country.flag}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <label
                      htmlFor="interpreter_email"
                      className="text-sm font-medium"
                    >
                      Email
                    </label>
                    <Input
                      id="interpreter_email"
                      name="interpreter_email"
                      placeholder="Enter email"
                      disabled={!isOnlyTier4}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.interpreter_email}
                      className={
                        errors.interpreter_email && touched.interpreter_email
                          ? "border-red-500"
                          : ""
                      }
                    />
                    <ErrorMessage
                      name="interpreter_email"
                      component="div"
                      className="text-xs text-red-500 font-light"
                    />
                  </div>
                  {/* Video Rate */}
                  {/* <div className="space-y-2">
                    <label htmlFor="video_rate" className="text-sm font-medium">
                      Video Rate
                    </label>
                    <Input
                      id="video_rate"
                      name="video_rate"
                      type="number"
                      step="0.01"
                      placeholder="Enter video rate"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.video_rate}
                      className={
                        errors.video_rate && touched.video_rate
                          ? "border-red-500"
                          : ""
                      }
                    />
                    <ErrorMessage
                      name="video_rate"
                      component="div"
                      className="text-xs text-red-500 font-light"
                    />
                  </div> */}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <label
                      htmlFor="interpreter_address"
                      className="text-sm font-medium"
                    >
                      Address
                    </label>
                    <Input
                      id="interpreter_address"
                      name="interpreter_address"
                      placeholder="Interpreter Address"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.interpreter_address}
                      className={
                        errors.interpreter_address &&
                        touched.interpreter_address
                          ? "border-red-500"
                          : ""
                      }
                    />
                    <ErrorMessage
                      name="interpreter_address"
                      component="div"
                      className="text-xs text-red-500 font-light"
                    />
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="interpreter_city"
                      className="text-sm font-medium"
                    >
                      Interpreter City
                    </label>
                    <Input
                      id="interpreter_city"
                      name="interpreter_city"
                      placeholder="Enter interpreter city"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.interpreter_city}
                      className={
                        errors.interpreter_city && touched.interpreter_city
                          ? "border-red-500"
                          : ""
                      }
                    />
                    <ErrorMessage
                      name="interpreter_city"
                      component="div"
                      className="text-xs text-red-500 font-light"
                    />
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="interpreter_state"
                      className="text-sm font-medium"
                    >
                      Interpreter State
                    </label>
                    <Input
                      id="interpreter_state"
                      name="interpreter_state"
                      placeholder="Enter interpreter state"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.interpreter_state}
                      className={
                        errors.interpreter_state && touched.interpreter_state
                          ? "border-red-500"
                          : ""
                      }
                    />
                    <ErrorMessage
                      name="interpreter_state"
                      component="div"
                      className="text-xs text-red-500 font-light"
                    />
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="interpreter_zip"
                      className="text-sm font-medium"
                    >
                      Interpreter ZIP
                    </label>
                    <Input
                      id="interpreter_zip"
                      name="interpreter_zip"
                      placeholder="Enter interpreter zip"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.interpreter_zip}
                      className={
                        errors.interpreter_zip && touched.interpreter_zip
                          ? "border-red-500"
                          : ""
                      }
                    />
                    <ErrorMessage
                      name="interpreter_zip"
                      component="div"
                      className="text-xs text-red-500 font-light"
                    />
                  </div>
                </div>
                {user.user_type !== "supervisor" && (
                  <div>
                  <div className="mt-6">
                    <h3 className="text-base font-semibold mb-4">
                      Voice Rates Per Tier
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {/* Tier 1 Voice Rate */}
                      <div className="space-y-2">
                        <label
                          htmlFor="tierRates.1.voice_rate"
                          className="text-sm font-medium"
                        >
                          Tier 1 Voice Rate ($/hr)
                        </label>
                        <Input
                          id="tierRates.1.voice_rate"
                          name="tierRates.1.voice_rate"
                          type="number"
                          step="0.01"
                          placeholder="Enter tier 1 rate"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.tierRates[1].voice_rate}
                          className={""}
                        />
                        <ErrorMessage
                          name="tierRates.1.voice_rate"
                          component="div"
                          className="text-xs text-red-500 font-light"
                        />
                      </div>

                      {/* Tier 2 Voice Rate */}
                      <div className="space-y-2">
                        <label
                          htmlFor="tierRates.2.voice_rate"
                          className="text-sm font-medium"
                        >
                          Tier 2 Voice Rate ($/min)
                        </label>
                        <Input
                          id="tierRates.2.voice_rate"
                          name="tierRates.2.voice_rate"
                          type="number"
                          step="0.01"
                          placeholder="Enter tier 2 rate"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.tierRates[2].voice_rate}
                          className={""}
                        />
                        <ErrorMessage
                          name="tierRates.2.voice_rate"
                          component="div"
                          className="text-xs text-red-500 font-light"
                        />
                      </div>

                      {/* Tier 3 Voice Rate */}
                      <div className="space-y-2">
                        <label
                          htmlFor="tierRates.3.voice_rate"
                          className="text-sm font-medium"
                        >
                          Tier 3 Voice Rate ($/min)
                        </label>
                        <Input
                          id="tierRates.3.voice_rate"
                          name="tierRates.3.voice_rate"
                          type="number"
                          step="0.01"
                          placeholder="Enter tier 3 rate"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.tierRates[3].voice_rate}
                          className={""}
                        />
                        <ErrorMessage
                          name="tierRates.3.voice_rate"
                          component="div"
                          className="text-xs text-red-500 font-light"
                        />
                      </div>

                      {/* Tier 4 Voice Rate */}
                      <div className="space-y-2">
                        <label
                          htmlFor="tierRates.4.voice_rate"
                          className="text-sm font-medium"
                        >
                          Tier 4 Voice Rate ($/min)
                        </label>
                        <Input
                          id="tierRates.4.voice_rate"
                          name="tierRates.4.voice_rate"
                          type="number"
                          step="0.01"
                          placeholder="Enter tier 4 rate"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.tierRates[4].voice_rate}
                          className={""}
                        />
                        <ErrorMessage
                          name="tierRates.4.voice_rate"
                          component="div"
                          className="text-xs text-red-500 font-light"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-6">
                    <h3 className="text-base font-semibold mb-4">
                      Video Rates Per Tier
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {/* Tier 1 Voice Rate */}
                      <div className="space-y-2">
                        <label
                          htmlFor="tierRates.1.voice_rate"
                          className="text-sm font-medium"
                        >
                          Tier 1 Video Rate ($/hr)
                        </label>
                        <Input
                          id="tierRates.1.video_rate"
                          name="tierRates.1.video_rate"
                          type="number"
                          step="0.01"
                          placeholder="Enter tier 1 rate"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.tierRates[1].video_rate}
                          className={""}
                        />
                        <ErrorMessage
                          name="tierRates.1.video_rate"
                          component="div"
                          className="text-xs text-red-500 font-light"
                        />
                      </div>

                      {/* Tier 2 Voice Rate */}
                      <div className="space-y-2">
                        <label
                          htmlFor="tierRates.2.video_rate"
                          className="text-sm font-medium"
                        >
                          Tier 2 Video Rate ($/min)
                        </label>
                        <Input
                          id="tierRates.2.video_rate"
                          name="tierRates.2.video_rate"
                          type="number"
                          step="0.01"
                          placeholder="Enter tier 2 rate"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.tierRates[2].video_rate}
                          className={""}
                        />
                        <ErrorMessage
                          name="tierRates.2.video_rate"
                          component="div"
                          className="text-xs text-red-500 font-light"
                        />
                      </div>

                      {/* Tier 3 Voice Rate */}
                      <div className="space-y-2">
                        <label
                          htmlFor="tierRates.3.video_rate"
                          className="text-sm font-medium"
                        >
                          Tier 3 Video Rate ($/min)
                        </label>
                        <Input
                          id="tierRates.3.video_rate"
                          name="tierRates.3.video_rate"
                          type="number"
                          step="0.01"
                          placeholder="Enter tier 3 rate"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.tierRates[3].video_rate}
                          className={""}
                        />
                        <ErrorMessage
                          name="tierRates.3.video_rate"
                          component="div"
                          className="text-xs text-red-500 font-light"
                        />
                      </div>

                  
                    </div>
                  </div>
                  </div>
                )}
                {/* Languages */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Languages</label>
                  {interpreter?.interpreter_tier == "4" && isOnlyTier4 && (
                    <Popover
                      open={openLanguageSelector}
                      onOpenChange={setOpenLanguageSelector}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={openLanguageSelector}
                          className="w-full justify-between"
                        >
                          {values.languages.length > 0
                            ? `${values.languages.length} language(s) selected`
                            : "Select languages..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0">
                        <Command>
                          <CommandInput placeholder="Search languages..." />
                          <CommandEmpty>No language found.</CommandEmpty>
                          <CommandList className="max-h-60 overflow-auto">
                            <CommandGroup>
                              {languages.map((language) => (
                                <CommandItem
                                  key={language.code}
                                  value={language.name}
                                  onSelect={() => {
                                    // Check if this language is already selected
                                    const isSelected = values.languages.some(
                                      (lang) => {
                                        // Handle both object format and string format
                                        if (
                                          typeof lang === "object" &&
                                          lang !== null
                                        ) {
                                          return (
                                            lang.attribute_value ===
                                            language.name
                                          );
                                        }
                                        return lang === language.code;
                                      }
                                    );

                                    let newLanguages;
                                    if (isSelected) {
                                      // Remove the language
                                      newLanguages = values.languages.filter(
                                        (lang) => {
                                          if (
                                            typeof lang === "object" &&
                                            lang !== null
                                          ) {
                                            return (
                                              lang.attribute_value !==
                                              language.name
                                            );
                                          }
                                          return lang !== language.code;
                                        }
                                      );
                                    } else {
                                      // Add the language in the format expected by the API
                                      newLanguages = [
                                        ...values.languages,
                                        {
                                          attribute_value: language.name,
                                          attribute_code: language.code,
                                        },
                                      ];
                                    }
                                    setFieldValue("languages", newLanguages);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      values.languages.some((lang) => {
                                        if (
                                          typeof lang === "object" &&
                                          lang !== null
                                        ) {
                                          return (
                                            lang.attribute_value ===
                                            language.name
                                          );
                                        }
                                        return lang === language.code;
                                      })
                                        ? "opacity-100"
                                        : "opacity-0"
                                    )}
                                  />
                                  {language.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  )}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {values.languages.map((lang, index) => {
                      // Handle both object format and string format
                      const displayName =
                        typeof lang === "object" && lang !== null
                          ? lang.attribute_value
                          : languages.find((l) => l.code === lang)?.name ||
                            lang;

                      const key =
                        typeof lang === "object" && lang !== null
                          ? lang.attribute_value
                          : lang;

                      return (
                        <div
                          key={`${key}-${index}`}
                          className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-md flex items-center"
                        >
                          {displayName}
                          {interpreter?.interpreter_tier == "4" &&
                            isOnlyTier4 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-4 w-4 p-0 ml-1"
                                onClick={() => {
                                  const newLanguages = values.languages.filter(
                                    (_, i) => i !== index
                                  );
                                  setFieldValue("languages", newLanguages);
                                }}
                              >
                                <span className="sr-only">Remove</span>
                                <span className="text-xs">×</span>
                              </Button>
                            )}
                        </div>
                      );
                    })}
                  </div>
                  <ErrorMessage
                    name="languages"
                    component="div"
                    className="text-xs text-red-500 font-light"
                  />
                </div>

                {/* Notes Section */}
                <div className="mt-6">
                  <CardTitle className="mb-2">Notes</CardTitle>

                  <Field name="notes">
                    {({ field, form }) => (
                      <Tiptap
                        value={field.value}
                        onChange={(newValue) =>
                          form.setFieldValue("notes", newValue)
                        }
                      />
                    )}
                  </Field>
                </div>
                <div className="mt-6">
                  <CardTitle className="mb-2"> Tiers</CardTitle>
                  <div className="flex gap-2">
                    {queues.map((queue) => (
                      <div
                        key={queue.cc_queue_id}
                        className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-md"
                      >
                        {queue.queue_name}
                      </div>
                    ))}
                  </div>
                </div>
                {/* Form Actions */}
                <div className="fixed bottom-0 left-0 right-0 w-full bg-white p-4 flex justify-end space-x-4 mt-6 border-t border-gray-200 shadow-lg   ">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/admin/interpreters")}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Saving..." : "Update Interpreter"}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>

      {/* Disable Tier 4 Confirmation Dialog */}
      <AlertDialog open={disableDialogOpen} onOpenChange={setDisableDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-500 !text-xl">
              Disable Tier 4 Status
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to disable <strong>Tier 4</strong> status
              for this interpreter? This action will delete the user if it does
              not exist in Zoom Users. Otherwise it will disable the{" "}
              <strong>Tier 4</strong> status.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disableLoading}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisableTier4}
              disabled={disableLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {disableLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Disabling...
                </>
              ) : (
                "Disable Tier 4"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EditInterpreterForm;
